import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Users, Search, Download, ChevronLeft, ChevronRight, X, Mail, Phone,
  Calendar, MapPin, Heart, Package, ShieldCheck, ShieldOff, Power, PowerOff,
  Trash2, KeyRound, Copy, AlertTriangle, TrendingUp, UserPlus, BadgeCheck,
} from 'lucide-react';
import api, { API_ORIGIN } from '../../lib/api';
import { formatPrice, formatDate, formatDateTime } from '../../lib/format';

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' },
  { id: 'verified', label: 'Verified' },
  { id: 'unverified', label: 'Unverified' },
];

const SORT_OPTIONS = [
  { id: 'createdAt', label: 'Newest' },
  { id: 'spend', label: 'Top spenders' },
  { id: 'orders', label: 'Most orders' },
];

const CustomerManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState('createdAt');

  const [selectedId, setSelectedId] = useState(null);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.set('page', page);
    p.set('limit', 20);
    if (query) p.set('q', query);
    if (statusFilter !== 'all') p.set('status', statusFilter);
    if (sort) p.set('sort', sort);
    return p.toString();
  }, [page, query, statusFilter, sort]);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [listRes, statsRes] = await Promise.all([
        api.get(`/customer/admin?${params}`),
        api.get('/customer/admin/stats'),
      ]);
      if (listRes.data?.success) {
        setCustomers(listRes.data.data || []);
        setPagination(listRes.data.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
      }
      if (statsRes.data?.success) {
        setStats(statsRes.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const qs = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const url = `${API_ORIGIN}/api/customer/admin/export${qs}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `owlfit-customers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert(`Export failed: ${err.message}`);
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-5xl font-black tracking-tighter mb-2 flex items-center gap-3">
            <Users size={42} /> CUSTOMERS
          </h1>
          <div className="inline-block bg-yellow-400 text-black px-4 py-1 font-mono font-bold text-xs border-2 border-black">
            CUSTOMER_MANAGEMENT_SYSTEM
          </div>
        </div>
        <button
          onClick={handleExport}
          className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 font-black uppercase text-sm border-4 border-yellow-400 hover:bg-yellow-400 hover:text-black transition-colors"
        >
          <Download size={18} /> Export CSV
        </button>
      </div>

      {/* Stats grid */}
      <StatGrid stats={stats} />

      {/* Filters */}
      <div className="bg-white border-4 border-black p-5 shadow-[8px_8px_0px_rgba(0,0,0,1)] mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-5">
            <label className="block text-[10px] font-black uppercase tracking-widest mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Name, email, or phone"
                className="w-full pl-10 pr-3 py-3 border-2 border-black font-mono text-sm focus:outline-none focus:bg-yellow-50"
              />
            </div>
          </div>
          <div className="lg:col-span-4">
            <label className="block text-[10px] font-black uppercase tracking-widest mb-1">Status</label>
            <div className="flex flex-wrap gap-1">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => { setStatusFilter(f.id); setPage(1); }}
                  className={`px-3 py-2 text-xs font-black uppercase border-2 border-black ${
                    statusFilter === f.id ? 'bg-black text-white' : 'bg-white hover:bg-yellow-200'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="lg:col-span-3">
            <label className="block text-[10px] font-black uppercase tracking-widest mb-1">Sort</label>
            <select
              value={sort}
              onChange={(e) => { setSort(e.target.value); setPage(1); }}
              className="w-full px-3 py-3 border-2 border-black font-bold bg-white"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border-4 border-black shadow-[8px_8px_0px_rgba(0,0,0,1)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-black text-white text-xs font-black uppercase tracking-widest">
                <th className="text-left p-4">Customer</th>
                <th className="text-left p-4 hidden md:table-cell">Joined</th>
                <th className="text-right p-4">Orders</th>
                <th className="text-right p-4">Spent</th>
                <th className="text-center p-4">Status</th>
                <th className="p-4 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array(6).fill(0).map((_, i) => (
                  <tr key={i} className="border-t-2 border-gray-100 animate-pulse">
                    <td className="p-4"><div className="h-4 bg-gray-200 w-40 mb-2" /><div className="h-3 bg-gray-100 w-56" /></td>
                    <td className="p-4 hidden md:table-cell"><div className="h-4 bg-gray-200 w-20" /></td>
                    <td className="p-4"><div className="h-4 bg-gray-200 w-8 ml-auto" /></td>
                    <td className="p-4"><div className="h-4 bg-gray-200 w-16 ml-auto" /></td>
                    <td className="p-4"><div className="h-6 bg-gray-200 w-16 mx-auto" /></td>
                    <td className="p-4"></td>
                  </tr>
                ))
              ) : error ? (
                <tr>
                  <td colSpan={6} className="p-10 text-center">
                    <AlertTriangle className="mx-auto mb-2 text-red-500" size={32} />
                    <div className="font-black text-lg">Failed to load</div>
                    <div className="text-xs font-mono text-gray-500">{error}</div>
                  </td>
                </tr>
              ) : customers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-10 text-center text-gray-500 font-bold">
                    No customers match these filters.
                  </td>
                </tr>
              ) : (
                customers.map((c) => (
                  <tr
                    key={c._id}
                    onClick={() => setSelectedId(c._id)}
                    className="border-t-2 border-gray-100 hover:bg-yellow-50 cursor-pointer transition-colors"
                  >
                    <td className="p-4">
                      <div className="font-black">{c.name || '—'}</div>
                      <div className="text-xs font-mono text-gray-500">{c.email}</div>
                      {c.phone && <div className="text-xs text-gray-500">+91 {c.phone}</div>}
                    </td>
                    <td className="p-4 hidden md:table-cell text-xs font-mono">
                      {formatDate(c.createdAt)}
                    </td>
                    <td className="p-4 text-right font-bold">{c.orderCount}</td>
                    <td className="p-4 text-right font-black">{formatPrice(c.totalSpent || 0)}</td>
                    <td className="p-4">
                      <div className="flex justify-center gap-1 flex-wrap">
                        <StatusBadge active={c.isActive} verified={c.isVerified} />
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <ChevronRight size={16} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between border-t-2 border-black p-4 bg-gray-50">
            <div className="text-xs font-mono">
              Page {pagination.page} of {pagination.pages} · {pagination.total} customers
            </div>
            <div className="flex gap-2">
              <button
                disabled={pagination.page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-2 border-2 border-black bg-white hover:bg-yellow-200 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1 font-bold text-xs"
              >
                <ChevronLeft size={14} /> Prev
              </button>
              <button
                disabled={pagination.page >= pagination.pages}
                onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                className="px-3 py-2 border-2 border-black bg-white hover:bg-yellow-200 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1 font-bold text-xs"
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Profile drawer */}
      {selectedId && (
        <CustomerProfileDrawer
          customerId={selectedId}
          onClose={() => setSelectedId(null)}
          onMutated={fetchCustomers}
        />
      )}
    </div>
  );
};

const StatusBadge = ({ active, verified }) => (
  <div className="inline-flex flex-col gap-1 items-end">
    <span
      className={`px-2 py-0.5 text-[10px] font-black uppercase border-2 border-black ${
        active ? 'bg-green-200' : 'bg-red-200'
      }`}
    >
      {active ? 'Active' : 'Inactive'}
    </span>
    {verified && (
      <span className="px-2 py-0.5 text-[10px] font-black uppercase border-2 border-black bg-blue-200 inline-flex items-center gap-1">
        <BadgeCheck size={10} /> Verified
      </span>
    )}
  </div>
);

const StatGrid = ({ stats }) => {
  const items = [
    { label: 'Total', value: stats?.total ?? '—', icon: <Users size={20} />, color: 'bg-white' },
    { label: 'Active', value: stats?.active ?? '—', icon: <Power size={20} />, color: 'bg-green-100' },
    { label: 'Verified', value: stats?.verified ?? '—', icon: <BadgeCheck size={20} />, color: 'bg-blue-100' },
    { label: 'New (30d)', value: stats?.newLast30d ?? '—', icon: <UserPlus size={20} />, color: 'bg-yellow-200' },
    { label: 'New this month', value: stats?.newThisMonth ?? '—', icon: <TrendingUp size={20} />, color: 'bg-pink-100' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
      {items.map((it) => (
        <div key={it.label} className={`border-4 border-black p-4 ${it.color}`}>
          <div className="flex items-center justify-between mb-2 text-black/70">{it.icon}<span className="text-[10px] font-black uppercase tracking-widest">{it.label}</span></div>
          <div className="text-3xl font-black tracking-tighter">{it.value}</div>
        </div>
      ))}
    </div>
  );
};

const CustomerProfileDrawer = ({ customerId, onClose, onMutated }) => {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [resetInfo, setResetInfo] = useState(null);

  useEffect(() => {
    if (!customerId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/customer/admin/${customerId}`)
      .then((res) => { if (!cancelled && res.data?.success) setCustomer(res.data.data); })
      .catch(() => { if (!cancelled) setCustomer(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [customerId]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const mutate = async (fn, optimistic) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fn();
      if (res?.data?.success) {
        if (optimistic) setCustomer((c) => ({ ...c, ...optimistic }));
        onMutated?.();
      }
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = () =>
    mutate(
      () => api.patch(`/customer/admin/${customerId}`, { isActive: !customer.isActive }),
      { isActive: !customer.isActive }
    );

  const toggleVerified = () =>
    mutate(
      () => api.patch(`/customer/admin/${customerId}`, { isVerified: !customer.isVerified }),
      { isVerified: !customer.isVerified }
    );

  const generateReset = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.post(`/customer/admin/${customerId}/reset-password`);
      if (res.data?.success) setResetInfo(res.data.data);
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const softDelete = async () => {
    if (!confirm('Deactivate this customer? They will not be able to log in.')) return;
    await mutate(() => api.delete(`/customer/admin/${customerId}`), { isActive: false });
  };

  const hardDelete = async () => {
    if (!confirm('PERMANENTLY DELETE this customer account? This cannot be undone.')) return;
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.delete(`/customer/admin/${customerId}?hard=true`);
      if (res.data?.success) {
        onMutated?.();
        onClose();
      }
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl h-full bg-white border-l-8 border-yellow-400 flex flex-col shadow-2xl">
        <header className="bg-black text-white p-6 border-b-4 border-yellow-400 flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-xs font-mono text-yellow-400 uppercase tracking-widest mb-1">Customer profile</div>
            <h2 className="text-3xl font-black tracking-tighter truncate">{customer?.name || '—'}</h2>
            <div className="text-xs font-mono text-gray-300 truncate">{customer?.email}</div>
          </div>
          <button
            onClick={onClose}
            className="p-2 bg-yellow-400 text-black hover:bg-white border-2 border-white"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </header>

        <div className="flex-grow overflow-y-auto">
          {loading || !customer ? (
            <div className="p-10 space-y-4 animate-pulse">
              <div className="h-24 bg-gray-100" />
              <div className="h-24 bg-gray-100" />
              <div className="h-24 bg-gray-100" />
            </div>
          ) : (
            <div className="p-6 space-y-6">
              {/* Quick actions */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <ActionButton
                  onClick={toggleActive}
                  disabled={busy}
                  active={customer.isActive}
                  iconOn={<Power size={16} />}
                  iconOff={<PowerOff size={16} />}
                  labelOn="Active"
                  labelOff="Inactive"
                />
                <ActionButton
                  onClick={toggleVerified}
                  disabled={busy}
                  active={customer.isVerified}
                  iconOn={<ShieldCheck size={16} />}
                  iconOff={<ShieldOff size={16} />}
                  labelOn="Verified"
                  labelOff="Unverified"
                />
                <button
                  onClick={generateReset}
                  disabled={busy}
                  className="px-3 py-3 border-2 border-black bg-white hover:bg-yellow-200 font-black text-xs uppercase inline-flex items-center gap-2 justify-center disabled:opacity-40"
                >
                  <KeyRound size={16} /> Reset PW
                </button>
                <button
                  onClick={softDelete}
                  disabled={busy || !customer.isActive}
                  className="px-3 py-3 border-2 border-black bg-red-500 text-white hover:bg-red-600 font-black text-xs uppercase inline-flex items-center gap-2 justify-center disabled:opacity-40"
                >
                  <Trash2 size={16} /> Ban
                </button>
              </div>

              {resetInfo && (
                <div className="bg-yellow-100 border-4 border-black p-4">
                  <div className="text-xs font-black uppercase mb-2">Reset link generated · expires {formatDateTime(resetInfo.expiresAt)}</div>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={resetInfo.resetUrl}
                      className="flex-grow p-2 border-2 border-black font-mono text-xs"
                      onFocus={(e) => e.target.select()}
                    />
                    <button
                      onClick={() => navigator.clipboard?.writeText(resetInfo.resetUrl)}
                      className="px-3 py-2 bg-black text-white border-2 border-black inline-flex items-center gap-1 text-xs font-bold"
                    >
                      <Copy size={14} /> Copy
                    </button>
                  </div>
                </div>
              )}

              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                <SummaryCard label="Orders" value={customer.summary?.orderCount ?? 0} />
                <SummaryCard label="Spent" value={formatPrice(customer.summary?.totalSpent || 0)} />
                <SummaryCard label="Last order" value={customer.summary?.lastOrderAt ? formatDate(customer.summary.lastOrderAt) : '—'} />
              </div>

              {/* Contact / meta */}
              <Section title="Account">
                <KV icon={<Mail size={14} />} label="Email" value={customer.email} />
                <KV icon={<Phone size={14} />} label="Phone" value={customer.phone ? `+91 ${customer.phone}` : '—'} />
                <KV icon={<Calendar size={14} />} label="Joined" value={formatDateTime(customer.createdAt)} />
                <KV icon={<Calendar size={14} />} label="Last login" value={customer.lastLogin ? formatDateTime(customer.lastLogin) : 'Never'} />
              </Section>

              {/* Email preferences */}
              <Section title="Preferences">
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <Pref label="Order updates" on={customer.emailPreferences?.orderUpdates ?? true} />
                  <Pref label="Promotions" on={customer.emailPreferences?.promotions ?? true} />
                  <Pref label="Newsletter" on={customer.emailPreferences?.newsletter ?? false} />
                </div>
              </Section>

              {/* Addresses */}
              {customer.addresses?.length > 0 && (
                <Section title={`Addresses (${customer.addresses.length})`}>
                  <div className="space-y-2">
                    {customer.addresses.map((a) => (
                      <div key={a._id} className="border-2 border-black p-3 text-sm bg-gray-50">
                        <div className="flex items-center gap-2 mb-1">
                          <MapPin size={14} />
                          <span className="font-black uppercase text-xs">{a.type}</span>
                          {a.isDefault && <span className="bg-yellow-400 text-black px-1.5 py-0.5 text-[9px] font-black uppercase border border-black">Default</span>}
                        </div>
                        <div className="font-bold">{a.name} · {a.phone}</div>
                        <div className="text-gray-700">{a.address}, {a.city}, {a.state} - {a.pincode}</div>
                        {a.landmark && <div className="text-xs text-gray-500">Landmark: {a.landmark}</div>}
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Wishlist */}
              {customer.wishlist?.length > 0 && (
                <Section title={`Wishlist (${customer.wishlist.length})`}>
                  <div className="flex flex-wrap gap-2">
                    {customer.wishlist.map((p) => (
                      <div key={p._id} className="border-2 border-black bg-white px-3 py-1.5 text-xs inline-flex items-center gap-1 font-bold">
                        <Heart size={12} className="text-red-500" />
                        {p.name}
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Orders */}
              <Section title={`Orders (${customer.orders?.length || 0})`}>
                {customer.orders?.length === 0 ? (
                  <div className="text-sm font-mono text-gray-500 italic">No orders yet.</div>
                ) : (
                  <div className="space-y-2">
                    {customer.orders.map((o) => (
                      <div key={o._id} className="border-2 border-black p-3 bg-white flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-mono text-xs">{o.orderNumber}</div>
                          <div className="text-xs text-gray-500">{formatDate(o.createdAt)} · {o.items?.length || 0} items</div>
                        </div>
                        <div className="text-right flex flex-col items-end gap-1">
                          <div className="font-black">{formatPrice(o.pricing?.total ?? 0)}</div>
                          <span className="text-[10px] font-black uppercase bg-yellow-400 px-1.5 py-0.5 border border-black">
                            {o.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Section>

              {/* Danger zone */}
              <div className="border-4 border-red-600 bg-red-50 p-4">
                <div className="font-black text-red-700 uppercase text-sm mb-2 flex items-center gap-2">
                  <AlertTriangle size={16} /> Danger zone
                </div>
                <p className="text-xs text-red-700 mb-3">
                  Permanently delete this account (GDPR). Their orders remain in the system but become unlinked.
                </p>
                <button
                  onClick={hardDelete}
                  disabled={busy}
                  className="px-4 py-2 bg-red-600 text-white font-black text-xs uppercase border-2 border-black hover:bg-red-700 disabled:opacity-40 inline-flex items-center gap-2"
                >
                  <Trash2 size={14} /> Delete permanently
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ActionButton = ({ onClick, disabled, active, iconOn, iconOff, labelOn, labelOff }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`px-3 py-3 border-2 border-black font-black text-xs uppercase inline-flex items-center gap-2 justify-center disabled:opacity-40 ${
      active ? 'bg-green-300 hover:bg-green-400' : 'bg-gray-200 hover:bg-gray-300'
    }`}
  >
    {active ? iconOn : iconOff}
    {active ? labelOn : labelOff}
  </button>
);

const Section = ({ title, children }) => (
  <div>
    <h3 className="text-xs font-black uppercase tracking-widest mb-2 text-gray-500">{title}</h3>
    <div className="border-2 border-black bg-white p-3">{children}</div>
  </div>
);

const KV = ({ icon, label, value }) => (
  <div className="flex items-center justify-between py-1.5 text-sm">
    <div className="flex items-center gap-2 text-gray-600">{icon}<span className="uppercase text-[10px] font-bold tracking-widest">{label}</span></div>
    <div className="font-mono text-right truncate ml-2">{value}</div>
  </div>
);

const SummaryCard = ({ label, value }) => (
  <div className="border-2 border-black bg-yellow-50 p-3 text-center">
    <div className="text-[10px] font-black uppercase tracking-widest text-gray-500">{label}</div>
    <div className="text-xl font-black mt-1">{value}</div>
  </div>
);

const Pref = ({ label, on }) => (
  <div className={`border-2 border-black p-2 text-center ${on ? 'bg-green-200' : 'bg-gray-100 opacity-60'}`}>
    <div className="text-[10px] font-bold uppercase">{label}</div>
    <Package size={14} className="mx-auto mt-1" />
  </div>
);

export default CustomerManagement;
